<template>
    <vx-expandable-card color="info">
        <template v-slot:content>
            <div class="flex flex-col gap-6">
                <div>
                    <span class="font-extrabold text-2xl text-slate-700">Membership Status Definitions</span>
                    <p class="text-sm text-slate-500 mb-0">
                        Configure how Intelligent Inbox treats membership status and steers AI replies.
                    </p>
                </div>

                <vx-table
                    v-if="definitions.length"
                    ref="sortableTable"
                    class="xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,2fr)_minmax(0,2fr)_auto]"
                    rowBreakpoint="xl"
                >
                    <vx-table-row isHeader>
                        <div></div>
                        <div>Name</div>
                        <div>Classification Steering</div>
                        <div>Message Steering</div>
                    </vx-table-row>

                    <membership-status-definition-row
                        v-for="(definition, index) in definitions"
                        :key="definition.id ?? definition.name + '-' + index"
                        class="membership-status-draggable"
                        :definition="definition"
                        :disabled="props.disabled"
                        @edit="openEdit(index)"
                        @delete="removeDefinition(index)"
                    />
                </vx-table>

                <div v-if="definitions.length < MAX_DEFINITIONS" class="flex">
                    <vx-box class="p-4">
                        <vx-button
                            data-test="new-definition-btn"
                            size="lg"
                            :disabled="props.disabled"
                            @click="openCreate"
                        >
                            New Definition
                            <font-awesome-icon :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </vx-box>
                </div>

                <vx-alert v-if="definitions.length >= MAX_DEFINITIONS" color="info" :closeable="false">
                    Maximum of {{ MAX_DEFINITIONS }} membership status definitions reached.
                </vx-alert>
            </div>
        </template>
    </vx-expandable-card>

    <modal-membership-status-definition
        v-model:visible="modalVisible"
        :definition="modalDefinition"
        :editIndex="modalEditIndex"
        @saved="onDefinitionSaved"
    />
</template>

<script lang="ts" setup>
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBox, VxButton, VxExpandableCard, VxSwal, VxTable, VxTableRow } from '@voxie/frontend-components';
import { useSortable } from '@vueuse/integrations/useSortable';
import { ref } from 'vue';
import type {
    MembershipStatusDefinitionFormData,
    MembershipStatusDefinitionType,
} from '~/services/intelligentInboxSettings/intelligentInboxSettingsClient.types';
import MembershipStatusDefinitionRow from './MembershipStatusDefinitionRow.vue';
import ModalMembershipStatusDefinition from './ModalMembershipStatusDefinition.vue';

const MAX_DEFINITIONS = 20;

const props = defineProps<{
    disabled?: boolean;
}>();

const definitions = defineModel<MembershipStatusDefinitionType[]>({ required: true });

const modalVisible = ref(false);
const modalDefinition = ref<MembershipStatusDefinitionType | null>(null);
const modalEditIndex = ref<number | null>(null);

const openCreate = (): void => {
    modalDefinition.value = null;
    modalEditIndex.value = null;
    modalVisible.value = true;
};

const openEdit = (index: number): void => {
    modalDefinition.value = definitions.value[index] ?? null;
    modalEditIndex.value = index;
    modalVisible.value = true;
};

const removeDefinition = (index: number): void => {
    const definition = definitions.value[index];

    VxSwal.confirmation(
        'Are you sure?',
        `Are you sure you want to delete "${definition?.name}"?`,
        'danger',
        async () => {
            definitions.value = definitions.value.filter((_, i) => i !== index);
        }
    );
};

const onDefinitionSaved = (data: MembershipStatusDefinitionFormData, editIndex: number | null): void => {
    if (null !== editIndex && undefined !== definitions.value[editIndex]) {
        definitions.value = definitions.value.map((definition, i) =>
            i === editIndex ? { ...definition, ...data } : definition
        );
    } else {
        definitions.value = [...definitions.value, { ...data } as MembershipStatusDefinitionType];
    }
    modalVisible.value = false;
};

const sortableTable = ref<InstanceType<typeof VxTable> | null>(null);

useSortable(sortableTable, definitions, {
    handle: '.membership-status-handle',
    draggable: '.membership-status-draggable',
    animation: 200,
    watchElement: true,
    onUpdate: (e: { oldDraggableIndex: number; newDraggableIndex: number }) => {
        const next = [...definitions.value];
        next.splice(e.newDraggableIndex, 0, ...next.splice(e.oldDraggableIndex, 1));
        definitions.value = next;
    },
} as Record<string, unknown>);
</script>
